import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Minister,
  MotMinistre,
  CabinetMinistre,
  StructureAdministrative,
} from "../../types/types";
import {
  getMinisters,
  getMotMinistre,
  getCabinetMinistre,
  getStructureAdministrative,
} from "../../services/api";
import { useLanguage } from "../../context/LanguageContext";
import { getTranslation } from "../../utils/translations";
import { getFileName, resolveMediaUrl } from "../../utils/media";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faUserTie,
  faSpinner,
  faFileAlt,
  faHandshake,
  faUsers,
  faDownload,
  faFilePdf,
  faFileImage,
  faBriefcase,
  faChevronDown,
  faChevronUp,
  faSitemap,
  faBuilding,
  faDiagramProject,
  faImage,
  faEye,
  faExpand,
  faCompress,
} from "@fortawesome/free-solid-svg-icons";

const MinisterPage: React.FC = () => {
  const [minister, setMinister] = useState<Minister | null>(null);
  const [welcomeMessage, setWelcomeMessage] = useState<MotMinistre | null>(
    null
  );
  const [cabinetMembers, setCabinetMembers] = useState<CabinetMinistre[]>([]);
  const [structureAdmin, setStructureAdmin] = useState<
    StructureAdministrative[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [expandedMember, setExpandedMember] = useState<number | null>(null);
  const [expandedStructure, setExpandedStructure] = useState<number | null>(
    null
  );
  const [expandedImage, setExpandedImage] = useState<number | null>(null);
  const { language } = useLanguage();

  // Clean HTML tags from text
  const cleanHtmlTags = (text: string) => {
    if (!text) return "";
    return text
      .replace(/<p><p>/g, "")
      .replace(/<p>/g, "")
      .replace(/<\/p>/g, "\n")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/&nbsp;/g, " ")
      .replace(/<[^>]*>/g, "")
      .trim();
  };

  // Function to detect file type from filename/url
  const detectFileType = (
    fileValue: string
  ): "image" | "pdf" | "unknown" => {
    if (!fileValue) return "unknown";
    const normalized = fileValue.toLowerCase();
    if (/\.(jpg|jpeg|png|gif|webp|svg)$/.test(normalized)) return "image";
    if (normalized.endsWith(".pdf")) return "pdf";
    return "unknown";
  };

  const fetchMinisterData = async () => {
    try {
      setLoading(true);

      // Fetch minister data
      const ministerResponse = await getMinisters();

      // Fetch welcome message
      const welcomeResponse = await getMotMinistre();

      // Fetch cabinet members
      const cabinetResponse = await getCabinetMinistre();

      // Fetch administrative structure
      const structureResponse = await getStructureAdministrative();

      if (ministerResponse.data && ministerResponse.data.length > 0) {
        const firstMinister = ministerResponse.data[0];
        const ministerData: Minister = {
          ...firstMinister,
          textarabe: firstMinister.textarabe || "", // Clean Arabic
          textfrancais: firstMinister.textfrancais || "", // Keep French HTML intact
          image:
            resolveMediaUrl(firstMinister.image) ||
            "https://via.placeholder.com/300x400/007a45/FFFFFF?text=وزير+العقارات",
        };
        setMinister(ministerData);
      }

      // Set welcome message if available
      if (welcomeResponse.data && welcomeResponse.data.length > 0) {
        const firstWelcomeMessage = welcomeResponse.data[0];
        setWelcomeMessage({
          ...firstWelcomeMessage,
          textarabe: cleanHtmlTags(firstWelcomeMessage.textarabe || ""),
          textfrancais: cleanHtmlTags(firstWelcomeMessage.textfrancais || ""),
        });
      }

      // Set cabinet members if available
      if (cabinetResponse.data && cabinetResponse.data.length > 0) {
        const cleanedCabinetMembers = cabinetResponse.data.map(
          (member: CabinetMinistre) => ({
            ...member,
            descarabe: cleanHtmlTags(member.descarabe || ""),
            descfrancais: cleanHtmlTags(member.descfrancais || ""),
            fichier: member.fichier ? resolveMediaUrl(member.fichier) : null,
          })
        );
        setCabinetMembers(cleanedCabinetMembers);
      }

      // Set administrative structure if available
      if (structureResponse.data && structureResponse.data.length > 0) {
        const cleanedStructure = structureResponse.data.map(
          (structure: StructureAdministrative) => {
            const fichierarabeType = detectFileType(
              structure.fichierarabe || ""
            );
            const fichierfrancaisType = detectFileType(
              structure.fichierfrancais || ""
            );

            return {
              ...structure,
              textarabe: cleanHtmlTags(structure.textarabe || ""),
              textfrancais: cleanHtmlTags(structure.textfrancais || ""),
              fichierarabe: structure.fichierarabe
                ? resolveMediaUrl(structure.fichierarabe)
                : "",
              fichierfrancais: structure.fichierfrancais
                ? resolveMediaUrl(structure.fichierfrancais)
                : "",
              fichierarabeType,
              fichierfrancaisType,
            };
          }
        );
        setStructureAdmin(cleanedStructure);
      }
    } catch (error) {
      console.error("Error fetching minister data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMinisterData();
  }, []);

  const toggleExpand = (id: number) => {
    if (expandedMember === id) {
      setExpandedMember(null);
    } else {
      setExpandedMember(id);
    }
  };

  const toggleStructureExpand = (id: number) => {
    if (expandedStructure === id) {
      setExpandedStructure(null);
    } else {
      setExpandedStructure(id);
    }
  };

  const toggleImageExpand = (id: number) => {
    if (expandedImage === id) {
      setExpandedImage(null);
    } else {
      setExpandedImage(id);
    }
  };

  // Function to get the appropriate title based on language
  const getCabinetMemberTitle = (member: CabinetMinistre) => {
    return language === "ar" ? member.titrearabe : member.titrefrancais;
  };

  // Function to get the appropriate description based on language
  const getCabinetMemberDescription = (member: CabinetMinistre) => {
    return language === "ar" ? member.descarabe : member.descfrancais;
  };

  // Function to get structure title based on language
  const getStructureTitle = (structure: any) => {
    return language === "ar" ? structure.titrearabe : structure.titrefrancais;
  };

  // Function to get structure text based on language
  const getStructureText = (structure: any) => {
    return language === "ar" ? structure.textarabe : structure.textfrancais;
  };

  // Function to get structure file based on language
  const getStructureFile = (structure: any) => {
    return language === "ar"
      ? structure.fichierarabe
      : structure.fichierfrancais;
  };

  // Function to get structure file type based on language
  const getStructureFileType = (structure: any) => {
    return language === "ar"
      ? structure.fichierarabeType
      : structure.fichierfrancaisType;
  };

  // Function to get file name for download
  const buildFileName = (title: string, fileType: string) => {
    const safeTitle = title.replace(/\s+/g, "_").replace(/[^\w-]/g, "");
    if (fileType === "image") {
      return `${safeTitle}.jpg`;
    } else if (fileType === "pdf") {
      return `${safeTitle}.pdf`;
    }
    return `${safeTitle}.bin`;
  };

  // Function to format file download
  const getFileDownloadLink = (member: CabinetMinistre) => {
    if (!member.fichier) return null;

    const fileType = detectFileType(member.fichier);
    const fileName = buildFileName(
      language === "ar" ? member.titrearabe : member.titrefrancais,
      fileType
    );

    return {
      href: member.fichier,
      fileName: fileName,
      type: fileType,
    };
  };

  // Function to format structure file download
  const getStructureFileDownloadLink = (structure: any) => {
    const file = getStructureFile(structure);
    const fileType = getStructureFileType(structure);

    if (!file) return null;

    const fileName = getFileName(file);

    return {
      href: file,
      fileName: fileName,
      type: fileType,
    };
  };

  // Direction and text alignment
  const getDirectionClass = () => (language === "ar" ? "rtl" : "ltr");
  const getTextAlignClass = () =>
    language === "ar" ? "text-right" : "text-left";

  if (loading) {
    return (
      <div className={`min-h-screen bg-gray-50 ${getDirectionClass()}`}>
        <div className="container mx-auto px-4 py-16">
          <div className="text-center py-16">
            <FontAwesomeIcon
              icon={faSpinner}
              className="text-mauritania-green-dark text-4xl animate-spin mb-4"
            />
            <p className="text-gray-600 text-xl">
              {getTranslation("loadingMinister", language)}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!minister) {
    return (
      <div className={`min-h-screen bg-gray-50 ${getDirectionClass()}`}>
        <div className="container mx-auto px-4 py-16">
          <div className="text-center py-20">
            <FontAwesomeIcon
              icon={faUserTie}
              className="text-gray-400 text-6xl mb-4"
            />
            <h2 className="text-2xl font-bold text-gray-600 mb-4">
              {getTranslation("noMinisterData", language)}
            </h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50 ${getDirectionClass()}`}>
      {/* Minister Header */}
      <section className="bg-gradient-to-r from-mauritania-green-dark to-mauritania-green text-white py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className={`md:w-2/3 ${getTextAlignClass()} mb-8 md:mb-0`}>
              <h1 className="text-4xl font-bold mb-4">
                {getTranslation("minister", language)}
              </h1>
              <p className="text-xl opacity-90">
                {language === "ar"
                  ? "وزارة العقارات وأملاك الدولة"
                  : "Ministère des Domaines et des Affaires Foncières"}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Minister Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Sidebar - Minister Info */}
            <div className="lg:w-1/3">
              <div className="bg-white rounded-lg shadow-lg sticky top-8 overflow-hidden">
                {/* Minister Photo */}
                <div className="relative">
                  <img
                    src={minister.image || undefined}
                    alt={getTranslation("minister", language)}
                    className="w-full h-80 object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6">
                    <h2 className="text-2xl font-bold text-white mb-2">
                      {language === "ar"
                        ? minister.titrearabe
                        : minister.titrefrancais}
                    </h2>
                    <p className="text-mauritania-gold font-semibold">
                      {language === "ar"
                        ? "وزير العقارات وأملاك الدولة"
                        : "Ministre des Domaines et des Affaires Foncières"}
                    </p>
                  </div>
                </div>

                {/* Quick Stats */}
              </div>

              {/* Quick Links */}
              <div className="mt-6 bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">
                  {language === "ar" ? "روابط سريعة" : "Liens Rapides"}
                </h3>
                <div className="space-y-3">
                  {cabinetMembers.length > 0 && (
                    <Link
                      to="#cabinet-section"
                      className="flex items-center text-mauritania-green hover:text-mauritania-green-dark"
                      onClick={(e) => {
                        e.preventDefault();
                        document
                          .getElementById("cabinet-section")
                          ?.scrollIntoView({ behavior: "smooth" });
                      }}
                    >
                      <FontAwesomeIcon icon={faUsers} className="ml-2" />
                      {language === "ar"
                        ? "مكتب الوزير"
                        : "Cabinet du Ministre"}
                    </Link>
                  )}
                  {structureAdmin.length > 0 && (
                    <Link
                      to="#structure-section"
                      className="flex items-center text-mauritania-green hover:text-mauritania-green-dark"
                      onClick={(e) => {
                        e.preventDefault();
                        document
                          .getElementById("structure-section")
                          ?.scrollIntoView({ behavior: "smooth" });
                      }}
                    >
                      <FontAwesomeIcon icon={faSitemap} className="ml-2" />
                      {language === "ar"
                        ? "الهيكل الإداري"
                        : "Structure Administrative"}
                    </Link>
                  )}
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:w-2/3">
              {/* Welcome Message */}
              <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
                <div className="flex items-center mb-6" id="motminister">
                  <div className="bg-mauritania-green-light p-3 rounded-full mr-4">
                    <FontAwesomeIcon
                      icon={faHandshake}
                      className="text-mauritania-green-dark text-2xl"
                    />
                  </div>
                  <h2 className="text-2xl font-bold text-mauritania-green-dark">
                    {welcomeMessage
                      ? language === "ar"
                        ? welcomeMessage.titrearabe
                        : welcomeMessage.titrefrancais
                      : language === "ar"
                      ? "كلمة ترحيب"
                      : "Mot de Bienvenue"}
                  </h2>
                </div>

                <div className="prose max-w-none">
                  {welcomeMessage ? (
                    <div className="text-gray-700 text-lg leading-relaxed mb-6 italic border-r-4 border-mauritania-gold pr-4">
                      {language === "ar" ? (
                        <div className="whitespace-pre-line">
                          {welcomeMessage.textarabe}
                        </div>
                      ) : (
                        <div className="whitespace-pre-line">
                          {welcomeMessage.textfrancais}
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-700 text-lg leading-relaxed mb-6 italic border-r-4 border-mauritania-gold pr-4">
                      {language === "ar"
                        ? "يسعدني أن أرحب بكم في الموقع الرسمي لوزارة العقارات وأملاك الدولة. نعمل بجد لخدمة المواطن وتحسين الخدمات العقارية."
                        : "Je suis heureux de vous souhaiter la bienvenue sur le site officiel du Ministère des Domaines et des Affaires Foncières. Nous travaillons dur pour servir le citoyen et améliorer les services immobiliers."}
                    </p>
                  )}
                </div>
              </div>

              {/* Full CV Section */}
              <div
                className="bg-white rounded-lg shadow-lg p-8 mb-8"
                id="ministerCV"
              >
                <div className="flex items-center mb-6">
                  <div className="bg-mauritania-green-light p-3 rounded-full mr-4">
                    <FontAwesomeIcon
                      icon={faFileAlt}
                      className="text-mauritania-green-dark text-2xl"
                    />
                  </div>
                  <h2 className="text-2xl font-bold text-mauritania-green-dark">
                    {getTranslation("ministerCV", language)}
                  </h2>
                </div>

                <div className="text-gray-700 leading-relaxed">
                  {language === "ar" ? (
                    minister.textarabe ? (
                      <div
                        className="prose prose-lg max-w-none leading-8"
                        dangerouslySetInnerHTML={{ __html: minister.textarabe }}
                      ></div>
                    ) : (
                      <p className="text-gray-500 italic text-center py-8">
                        لا تتوفر السيرة الذاتية الكاملة حالياً. سيتم إضافة
                        المعلومات قريباً.
                      </p>
                    )
                  ) : minister.textfrancais ? (
                    <div
                      className="prose prose-lg max-w-none leading-8"
                      dangerouslySetInnerHTML={{
                        __html: minister.textfrancais,
                      }}
                    ></div>
                  ) : (
                    <p className="text-gray-500 italic text-center py-8">
                      Le curriculum vitae complet n'est pas disponible pour le
                      moment. Les informations seront ajoutées prochainement.
                    </p>
                  )}
                </div>
              </div>

              {/* Cabinet Ministre Section */}
              {cabinetMembers.length > 0 && (
                <div
                  id="cabinet"
                  className="bg-white rounded-lg shadow-lg p-8 mb-8"
                >
                  <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center">
                      <div className="bg-mauritania-green-light p-3 rounded-full mr-4">
                        <FontAwesomeIcon
                          icon={faUsers}
                          className="text-mauritania-green-dark text-2xl"
                        />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-mauritania-green-dark">
                          {language === "ar"
                            ? "مكتب الوزير"
                            : "Cabinet du Ministre"}
                        </h2>
                        <p className="text-gray-600 mt-1">
                          {language === "ar"
                            ? "فريق العمل المعاون للوزير"
                            : "Équipe de travail collaborant avec le ministre"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {cabinetMembers.map((member) => {
                      const title = getCabinetMemberTitle(member);
                      const description = getCabinetMemberDescription(member);
                      const fileLink = getFileDownloadLink(member);

                      return (
                        <div
                          key={member.id}
                          className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all duration-300"
                        >
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h3 className="text-xl font-bold text-gray-800 mb-2">
                                {title}
                              </h3>
                            </div>
                            {description && description.length > 100 && (
                              <button
                                onClick={() =>
                                  member.id && toggleExpand(member.id)
                                }
                                className="text-mauritania-green hover:text-mauritania-green-dark p-2 rounded-full hover:bg-mauritania-green-light transition-colors"
                              >
                                <FontAwesomeIcon
                                  icon={
                                    expandedMember === member.id
                                      ? faChevronUp
                                      : faChevronDown
                                  }
                                />
                              </button>
                            )}
                          </div>

                          {description && (
                            <p className="text-gray-700">
                              {expandedMember === member.id
                                ? description
                                : description.substring(0, 120) +
                                  (description.length > 120 ? "..." : "")}
                            </p>
                          )}

                          {(expandedMember === member.id || fileLink) && (
                            <div className="mt-4 pt-4 border-t border-gray-100">
                              <div className="flex justify-between items-center">
                                {fileLink && (
                                  <a
                                    href={fileLink.href}
                                    download={fileLink.fileName}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center px-3 py-2 bg-mauritania-green text-white rounded-lg hover:bg-mauritania-green-dark transition-colors text-sm"
                                  >
                                    <FontAwesomeIcon
                                      icon={
                                        fileLink.type === "pdf"
                                          ? faFilePdf
                                          : faFileImage
                                      }
                                      className="ml-2"
                                    />
                                    {fileLink.type === "pdf"
                                      ? language === "ar"
                                        ? "تحميل الوثيقة"
                                        : "Télécharger le document"
                                      : language === "ar"
                                      ? "تحميل الصورة"
                                      : "Télécharger l'image"}
                                  </a>
                                )}
                                {description && description.length > 120 && (
                                  <button
                                    onClick={() =>
                                      member.id && toggleExpand(member.id)
                                    }
                                    className="text-mauritania-green text-sm hover:text-mauritania-green-dark"
                                  >
                                    {expandedMember === member.id
                                      ? language === "ar"
                                        ? "عرض أقل"
                                        : "Voir moins"
                                      : language === "ar"
                                      ? "عرض المزيد"
                                      : "Voir plus"}
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Structure Administrative Section */}
              {structureAdmin.length > 0 && (
                <div
                  id="structure-section"
                  className="bg-white rounded-lg shadow-lg p-8"
                >
                  <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center">
                      <div className="bg-green-100 p-3 rounded-full mr-4">
                        <FontAwesomeIcon
                          icon={faSitemap}
                          className="text-green-600 text-2xl"
                        />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-gray-800">
                          {language === "ar"
                            ? "الهيكل الإداري"
                            : "Structure Administrative"}
                        </h2>
                        <p className="text-gray-600 mt-1">
                          {language === "ar"
                            ? "التنظيم الهيكلي والإداري للوزارة"
                            : "Organisation structurelle et administrative du ministère"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-8">
                    {structureAdmin.map((structure: any) => {
                      const title = getStructureTitle(structure);
                      const text = getStructureText(structure);
                      const fileLink = getStructureFileDownloadLink(structure);
                      const fileType = getStructureFileType(structure);
                      const fileUrl = getStructureFile(structure);

                      return (
                        <div
                          key={structure.id}
                          className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all duration-300"
                        >
                          <div className="flex justify-between items-start mb-6">
                            <div className="flex-1">
                              <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                                <FontAwesomeIcon
                                  icon={faBuilding}
                                  className="ml-2 text-green-500"
                                />
                                {title}
                              </h3>
                              {text && (
                                <div className="text-gray-700 mb-4">
                                  {expandedStructure === structure.id ? (
                                    <div className="whitespace-pre-line">
                                      {text}
                                    </div>
                                  ) : (
                                    <div className="line-clamp-3">{text}</div>
                                  )}
                                </div>
                              )}
                            </div>
                            {text && text.length > 150 && (
                              <button
                                onClick={() =>
                                  structure.id &&
                                  toggleStructureExpand(structure.id)
                                }
                                className="text-green-600 hover:text-green-800 p-2 rounded-full hover:bg-green-50 transition-colors ml-4"
                              >
                                <FontAwesomeIcon
                                  icon={
                                    expandedStructure === structure.id
                                      ? faChevronUp
                                      : faChevronDown
                                  }
                                />
                              </button>
                            )}
                          </div>

                          {/* File Display Section */}
                          {fileUrl && (
                            <div className="mt-6 pt-6 border-t border-gray-100">
                              <div className="flex flex-col lg:flex-row gap-6">
                                {/* File Preview */}
                                <div className="lg:w-2/3">
                                  <div className="flex items-center justify-between mb-3">
                                    <h4 className="font-semibold text-gray-700 flex items-center">
                                      <FontAwesomeIcon
                                        icon={
                                          fileType === "image"
                                            ? faImage
                                            : faFilePdf
                                        }
                                        className="ml-2"
                                      />
                                      {fileType === "image"
                                        ? language === "ar"
                                          ? "معاينة الهيكل التنظيمي"
                                          : "Aperçu de la structure"
                                        : language === "ar"
                                        ? "معاينة الوثيقة"
                                        : "Aperçu du document"}
                                    </h4>
                                    {fileType === "image" && (
                                      <button
                                        onClick={() =>
                                          structure.id &&
                                          toggleImageExpand(structure.id)
                                        }
                                        className="text-green-600 hover:text-green-800 text-sm flex items-center"
                                      >
                                        <FontAwesomeIcon
                                          icon={
                                            expandedImage === structure.id
                                              ? faCompress
                                              : faExpand
                                          }
                                          className="ml-2"
                                        />
                                        {expandedImage === structure.id
                                          ? language === "ar"
                                            ? "تصغير"
                                            : "Réduire"
                                          : language === "ar"
                                          ? "تكبير"
                                          : "Agrandir"}
                                      </button>
                                    )}
                                  </div>

                                  <div
                                    className={`relative bg-gray-100 rounded-lg overflow-hidden ${
                                      expandedImage === structure.id
                                        ? "min-h-[400px]"
                                        : "h-64"
                                    }`}
                                  >
                                    {fileType === "image" ? (
                                      <img
                                        src={fileUrl}
                                        alt={title}
                                        className={`w-full h-full object-contain ${
                                          expandedImage === structure.id
                                            ? "object-scale-down"
                                            : "object-cover"
                                        }`}
                                      />
                                    ) : (
                                      <div className="h-full flex flex-col items-center justify-center p-6">
                                        <FontAwesomeIcon
                                          icon={faFilePdf}
                                          className="text-red-500 text-5xl mb-4"
                                        />
                                        <p className="text-gray-600 text-center mb-4">
                                          {language === "ar"
                                            ? "مستند الهيكل التنظيمي - اضغط للتحميل"
                                            : "Document de structure organisationnelle - Cliquez pour télécharger"}
                                        </p>
                                        <div className="text-sm text-gray-500">
                                          {language === "ar"
                                            ? "PDF Document"
                                            : "Document PDF"}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Download Section */}
                                <div className="lg:w-1/3">
                                  <div className="bg-green-50 rounded-lg p-5 h-full">
                                    <h4 className="font-semibold text-gray-800 mb-4">
                                      {language === "ar"
                                        ? "خيارات التحميل"
                                        : "Options de téléchargement"}
                                    </h4>

                                    <div className="space-y-4">
                                      <div>
                                        <p className="text-sm text-gray-600 mb-2">
                                          {language === "ar"
                                            ? "نوع الملف:"
                                            : "Type de fichier:"}
                                        </p>
                                        <div className="flex items-center">
                                          <FontAwesomeIcon
                                            icon={
                                              fileType === "image"
                                                ? faImage
                                                : faFilePdf
                                            }
                                            className={`ml-2 ${
                                              fileType === "image"
                                                ? "text-green-500"
                                                : "text-red-500"
                                            }`}
                                          />
                                          <span className="text-gray-700">
                                            {fileType === "image"
                                              ? language === "ar"
                                                ? "صورة"
                                                : "Image"
                                              : language === "ar"
                                              ? "مستند PDF"
                                              : "Document PDF"}
                                          </span>
                                        </div>
                                      </div>

                                      {fileLink && (
                                        <a
                                          href={fileLink.href}
                                          download={fileLink.fileName}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center justify-center w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                                        >
                                          <FontAwesomeIcon
                                            icon={faDownload}
                                            className="ml-2"
                                          />
                                          {language === "ar"
                                            ? "تحميل الملف"
                                            : "Télécharger le fichier"}
                                        </a>
                                      )}

                                      {fileType === "image" && (
                                        <button
                                          onClick={() =>
                                            structure.id &&
                                            toggleImageExpand(structure.id)
                                          }
                                          className="w-full px-4 py-3 border border-green-600 text-green-600 rounded-lg hover:bg-green-50 transition-colors flex items-center justify-center"
                                        >
                                          <FontAwesomeIcon
                                            icon={
                                              expandedImage === structure.id
                                                ? faCompress
                                                : faExpand
                                            }
                                            className="ml-2"
                                          />
                                          {expandedImage === structure.id
                                            ? language === "ar"
                                              ? "تصغير الصورة"
                                              : "Réduire l'image"
                                            : language === "ar"
                                            ? "تكبير الصورة"
                                            : "Agrandir l'image"}
                                        </button>
                                      )}

                                      <div className="text-xs text-gray-500 pt-4">
                                        <p>
                                          {language === "ar"
                                            ? "💡 يمكنك النقر على الصورة للتكبير"
                                            : "💡 Vous pouvez cliquer sur l'image pour zoomer"}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Image Modal for Expanded View */}
      {expandedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setExpandedImage(null)}
        >
          <div className="relative max-w-6xl max-h-[90vh]">
            <button
              className="absolute top-4 left-4 text-white bg-black bg-opacity-50 rounded-full p-3 hover:bg-opacity-70 z-10"
              onClick={() => setExpandedImage(null)}
            >
              <FontAwesomeIcon icon={faCompress} size="lg" />
            </button>
            <img
              src={
                structureAdmin.find((s) => s.id === expandedImage)
                  ?.fichierarabe || ""
              }
              alt="Expanded view"
              className="max-w-full max-h-[85vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MinisterPage;
